import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Audits and Assurances | How revenue works",
  "includeToc": true,
  "description": "Data about revenue from the extractive industries is subject to a number of controls, standards, and regulations in the United States. Companies and governments are accountable to internal and external oversight that ensures correct reporting and publication of payments.",
  "tags": ["How revenue works", "Audits", "Assurances", "Standards", "Oversight", "Regulations"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Box = makeShortcode("Box");
const EitiArchive = makeShortcode("EitiArchive");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/database/how-revenue-works/"
      }}>{`How revenue works /`}</a></p>
    <h1 {...{
      "id": "Audits-and-Assurances",
      "style": {
        "position": "relative"
      }
    }}>{`Audits and Assurances`}</h1>
    <p>{`Data about revenue from the extractive industries is bound by rules and regulations in the United States. Companies and governments are accountable to internal and external oversight. This ensures correct reporting and publication of payments.`}</p>
    <h2 {...{
      "id": "Tracking-and-verifying-payments",
      "style": {
        "position": "relative"
      }
    }}>{`Tracking and verifying payments`}</h2>
    <p>{`Companies and government agencies are each legally responsible for reporting payment data. Reported data must be both accurate and verifiable. Controls and standards help companies report correct data on the first attempt. Reports are verified after submission. Independent audits help ensure compliance with internal and external standards.`}</p>
    <Box pl={4} mt={3} mdxType="Box">
      <p><a parentName="p" {...{
          "href": "/sites/database/downloads/archive/USEITI_budget-audit-factsheet_2017-08-09.pdf"
        }}>{`Download audit and assurance processes factsheet (pdf, 1.1 MB)`}</a></p>
      <EitiArchive mdxType="EitiArchive" />
    </Box>
    <h2 {...{
      "id": "Standards-reporting-and-oversight",
      "style": {
        "position": "relative"
      }
    }}>{`Standards, reporting, and oversight`}</h2>
    <h3 {...{
      "id": "Company-standards-and-oversight",
      "style": {
        "position": "relative"
      }
    }}>{`Company standards and oversight`}</h3>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.aicpa.org/home"
        }}>{`American Institute of CPAs`}</a>{` publishes `}<a parentName="li" {...{
          "href": "https://pcaobus.org/Standards/Auditing/Pages/AU150.aspx"
        }}>{`Generally Accepted Auditing Standards`}</a>{`.`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "http://www.fasb.org/jsp/FASB/Page/LandingPage&cid=1175805317350"
        }}>{`Financial Accounting Standards Board`}</a>{` Accounting Standards Codification is the single official source of authoritative, nongovernmental U.S. generally accepted accounting principles (GAAP).`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://pcaobus.org/Pages/default.aspx"
        }}>{`Public Company Accounting Oversight Board`}</a>{` helps confirm that registered public accounting firms are operating in accordance with auditing standards.`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.sec.gov/"
        }}>{`Security and Exchange Commission`}</a>{` maintains a searchable database of filings by public companies, called `}<a parentName="li" {...{
          "href": "https://www.sec.gov/edgar/searchedgar/companysearch.html"
        }}>{`EDGAR`}</a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "Federal-standards-and-audits",
      "style": {
        "position": "relative"
      }
    }}>{`Federal standards and audits`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://obamawhitehouse.archives.gov/omb/circulars_a123_rev"
        }}>{`OMB Circular No. A-123`}</a>{` prescribes responsibilities for establishing and maintaining effective internal controls and financial management systems.`}</li>
      <li parentName="ul">{`The roles and responsibilities of each agency are governed by `}<a parentName="li" {...{
          "href": "https://www.congress.gov/bill/101st-congress/house-bill/5687/text/enr"
        }}>{`The Chief Financial Officers Act: A Mandate for Federal Financial Management Reform`}</a>{`.`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "http://www.gao.gov/"
        }}>{`Government Accountability Office`}</a>{` (GAO) publishes `}<em parentName="li">{`Standards for Internal Control in the Federal Government`}</em>{`.  This is known as the `}<a parentName="li" {...{
          "href": "http://www.gao.gov/greenbook/overview"
        }}>{`Green Book`}</a>{`.`}</li>
      <li parentName="ul">{`Audits are supported by standards like the GAO's `}<em parentName="li">{`Generally Accepted Government Auditing Standards`}</em>{`.  This is known as the `}<a parentName="li" {...{
          "href": "http://www.gao.gov/yellowbook/overview"
        }}>{`Yellow Book`}</a>{`.`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.fiscal.treasury.gov/fsreports/ref/ussgl/ussgl_home.htm"
        }}>{`U.S. Standard General Ledger`}</a>{` provides a uniform chart of accounts and technical guidance for standardizing federal agency accounting to OMB and the U.S. Treasury.`}</li>
      <li parentName="ul">{`Department of the Interior audit results are publicly disclosed on an annual basis by the `}<a parentName="li" {...{
          "href": "https://www.doioig.gov/reports"
        }}>{`Office of Inspector General`}</a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "Federal-revenue-reporting",
      "style": {
        "position": "relative"
      }
    }}>{`Federal revenue reporting`}</h3>
    <p>{`Many federal agencies make their reports on payments received from U.S. extractive industries available publicly online:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.irs.gov/publications/"
        }}>{`IRS publications`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://fiscal.treasury.gov/reports-statements/combined-statement/"
        }}>{`U.S. Treasury: Combined statement of receipts, outlays, and balances`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.blm.gov/learn/blm-library/agency-publications"
        }}>{`Bureau of Land Management (BLM) publications`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.osmre.gov/resources/InformationFor.shtm"
        }}>{`Office of Surface Mining Reclamation and Enforcement (OSMRE) resources`}</a></li>
    </ul>
    <p>{`The U.S. government is subject to numerous laws governing data publication, including:`}</p>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://obamawhitehouse.archives.gov/omb/financial_ffs_ffmia"
        }}>{`Federal Financial Management Improvement Act of 1996`}</a>{` ensures federal financial managements systems provide accurate, reliable, and timely information to government managers.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.law.cornell.edu/uscode/text/26/6103"
        }}>{`26 U.S. Code § 6103`}</a>{` requires confidentiality of tax returns and return information.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.law.cornell.edu/uscode/text/18/1905"
        }}>{`18 U.S. Code § 1905`}</a>{` punishes disclosure of proprietary information by a government employee.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      